import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackBookingConversion } from "@/lib/gtag";
import { SITE } from "@/data/content";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const fmtPrice = (p) => (p === 0 ? "Gratuita" : `${p}€`);
const toISO = (d) => {
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
};
const weekdays = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const months = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];
const prettyDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${weekdays[d.getDay()]}, ${d.getDate()} de ${months[d.getMonth()]}`;
};

const steps = ["Serviço", "Data e hora", "Os seus dados"];

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      const pre = params.get("servico");
      if (pre) {
        const s = r.data.find((x) => x.id === pre);
        if (s) { setService(s); setStep(1); }
      }
    });
  }, [params]);

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true);
    setTime(null);
    api.get(`/availability?date=${toISO(date)}`)
      .then((r) => setSlots(r.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const canContinue = useMemo(() => {
    if (step === 0) return !!service;
    if (step === 1) return !!date && !!time;
    if (step === 2) return form.name && form.email && form.phone;
    return false;
  }, [step, service, date, time, form]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: service.id,
        date: toISO(date),
        time,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
      });
      // Google Ads conversion — only after the booking is confirmed saved.
      // No patient personal data is sent (only internal id + price).
      trackBookingConversion({ id: data?.booking?.id, price: service.price });
      navigate(`/marcacao/confirmada`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Não foi possível concluir a marcação.");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900" data-testid="booking-back">
          <ArrowLeft className="w-4 h-4" /> Voltar ao início
        </Link>

        <div className="mt-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="text-xs uppercase tracking-[0.25em] text-rose-800">Marcação</p>
            <h1 className="font-display font-light text-4xl sm:text-5xl text-stone-900 mt-3">
              Agende a sua consulta
            </h1>

            <div className="flex items-center gap-3 mt-8">
              {steps.map((s, i) => (
                <div key={s} className="flex items-center gap-3">
                  <div className={`flex items-center gap-2 text-sm ${i <= step ? "text-rose-800" : "text-stone-400"}`}>
                    <span className={`grid place-items-center w-7 h-7 rounded-full text-xs border ${i < step ? "bg-rose-800 text-white border-rose-800" : i === step ? "border-rose-800" : "border-stone-200"}`}>
                      {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className="hidden sm:inline">{s}</span>
                  </div>
                  {i < steps.length - 1 && <span className="w-6 h-px bg-stone-200" />}
                </div>
              ))}
            </div>

            <div className="mt-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35 }}
                >
                  {step === 0 && (
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="step-service">
                      {services.map((s) => (
                        <button
                          key={s.id}
                          data-testid={`booking-service-${s.id}`}
                          onClick={() => setService(s)}
                          className={`text-left rounded-2xl border p-6 transition-all duration-300 ${
                            service?.id === s.id ? "border-rose-800 bg-rose-50/60 ring-1 ring-rose-800" : "border-rose-100 hover:border-rose-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <h3 className="font-display text-xl text-stone-900">{s.name}</h3>
                            <span className="font-display text-2xl text-rose-800">{fmtPrice(s.price)}</span>
                          </div>
                          <p className="text-sm text-stone-500 mt-2">{s.description}</p>
                          <span className="inline-flex items-center gap-1.5 text-xs text-stone-400 mt-4">
                            <Clock className="w-3.5 h-3.5" /> {s.duration}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {step === 1 && (
                    <div className="grid md:grid-cols-2 gap-8" data-testid="step-datetime">
                      <div className="rounded-2xl border border-rose-100 p-4 w-fit">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => {
                            const today = new Date(); today.setHours(0,0,0,0);
                            return d < today || d.getDay() === 0 || d.getDay() === 6;
                          }}
                          data-testid="booking-calendar"
                        />
                      </div>
                      <div>
                        <h3 className="text-sm uppercase tracking-widest text-stone-400 mb-4">Horários</h3>
                        {!date && <p className="text-stone-500 text-sm">Selecione primeiro um dia.</p>}
                        {date && loadingSlots && (
                          <div className="flex items-center gap-2 text-stone-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>
                        )}
                        {date && !loadingSlots && slots.length === 0 && (
                          <p className="text-stone-500 text-sm">Sem horários disponíveis neste dia.</p>
                        )}
                        <div className="grid grid-cols-3 gap-2.5">
                          {date && !loadingSlots && slots.map((sl) => (
                            <button
                              key={sl.time}
                              disabled={!sl.available}
                              data-testid={`booking-slot-${sl.time}`}
                              onClick={() => setTime(sl.time)}
                              className={`rounded-full py-2.5 text-sm border transition-colors ${
                                time === sl.time ? "bg-rose-800 text-white border-rose-800"
                                : sl.available ? "border-rose-200 text-stone-700 hover:border-rose-500"
                                : "border-stone-100 text-stone-300 line-through cursor-not-allowed"
                              }`}
                            >
                              {sl.time}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="max-w-lg space-y-5" data-testid="step-details">
                      <div>
                        <Label htmlFor="name">Nome completo</Label>
                        <Input id="name" data-testid="booking-name" value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <Label htmlFor="email">Email</Label>
                          <Input id="email" type="email" data-testid="booking-email" value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
                        </div>
                        <div>
                          <Label htmlFor="phone">Telemóvel</Label>
                          <Input id="phone" data-testid="booking-phone" value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="notes">Mensagem (opcional)</Label>
                        <Textarea id="notes" data-testid="booking-notes" value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" rows={4}
                          placeholder="Partilhe o que quiser antes da consulta." />
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-12 flex items-center gap-4">
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} data-testid="booking-prev"
                  className="rounded-full border border-stone-200 px-6 py-3 text-sm text-stone-700 hover:border-stone-400 transition-colors">
                  Anterior
                </button>
              )}
              {step < 2 && (
                <button onClick={() => setStep(step + 1)} disabled={!canContinue} data-testid="booking-next"
                  className="btn-rose rounded-full bg-rose-800 text-white px-8 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                  Continuar
                </button>
              )}
              {step === 2 && (
                <button onClick={submit} disabled={!canContinue || submitting} data-testid="booking-submit"
                  className="btn-rose inline-flex items-center gap-2 rounded-full bg-rose-800 text-white px-8 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirmar marcação
                </button>
              )}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-3xl bg-[#FAF7F7] border border-rose-100 p-8 lg:sticky lg:top-10" data-testid="booking-summary">
              <h3 className="font-display text-2xl text-stone-900">Resumo</h3>
              <div className="mt-6 space-y-4 text-sm">
                <Row label="Consulta" value={service?.name || "—"} />
                <Row label="Duração" value={service?.duration || "—"} />
                <Row label="Data" value={date ? prettyDate(toISO(date)) : "—"} />
                <Row label="Hora" value={time || "—"} />
              </div>
              <div className="mt-6 pt-6 border-t border-rose-100 flex items-center justify-between">
                <span className="text-stone-500 text-sm">Total</span>
                <span className="font-display text-3xl text-rose-800">
                  {service ? fmtPrice(service.price) : "—"}
                </span>
              </div>
              {service && (
                <p className="mt-4 text-xs text-stone-400 leading-relaxed">
                  O pagamento é efetuado no local, no dia da consulta.
                </p>
              )}
              <p className="mt-6 text-xs text-stone-400">
                Dúvidas antes de começar?{" "}
                <a
                  href={`https://wa.me/${SITE.phone.replace(/\D/g, "")}?text=${encodeURIComponent("Olá Inês, tenho algumas dúvidas antes de marcar a minha consulta.")}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="booking-whatsapp-link"
                  className="text-[#128C4B] underline underline-offset-2"
                >
                  Fale connosco no WhatsApp
                </a>{" "}
                ou escreva para {SITE.email}
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-stone-400">{label}</span>
    <span className="text-stone-800 text-right">{value}</span>
  </div>
);
